import { Alert, Button, classnames, Title } from '@terveystalo/design-system-react-components';
import { ApiModelType } from 'model/api';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressBar } from 'ui/questionary/ProgressBar';
import { Question } from 'ui/questionary/Question';
import { useApiData } from 'utils/api/helpers';
import { useAuthContext } from 'utils/auth/auth-context';
import { sendAppError, sendMessage } from 'utils/communication/useSendMessages';
import { useUiContext } from 'utils/react/ui-context';
import { Language, ownApiAnswer, ownApiAnswerWithValidation } from 'utils/types/common';

type Props = {
  onClickNext: (answer: ownApiAnswer[]) => void;
  handleSessionUpdate: (session: string) => void;
  session: string;
  selectedSymptoms: string[];
};

type FormStatus = {
  error: boolean;
  message?: 'error_missing_information' | 'error_remote_data_failure';
  invalidFields?: ownApiAnswerWithValidation[];
};

const formStatusInit: FormStatus = {
  error: false,
};

export const QuestionnaireForm: FC<Props> = ({ onClickNext, selectedSymptoms, handleSessionUpdate, session }) => {
  const { apiClient } = useUiContext();
  const { t, i18n } = useTranslation();
  const selectedLanguage = i18n.language as Language;
  const { authState } = useAuthContext();
  const [questions, setQuestions] = useState<ApiModelType['Question'][]>([]);
  const [answersWithValidation, setAnswersWithValidation] = useState<ownApiAnswerWithValidation[]>([]);
  const [formStatus, setFormStatus] = useState<FormStatus>(formStatusInit);
  const age = authState.type === 'LoggedIn' ? authState.age ?? undefined : undefined;
  const gender = authState.type === 'LoggedIn' ? authState.gender ?? undefined : undefined;
  const [startQuestionnaireData] = useApiData('startQuestionnaire', [selectedLanguage, selectedSymptoms, age, gender]);

  const handleQuestionUpdate = function (answer: ownApiAnswerWithValidation) {
    const filteredAnswers = answersWithValidation.filter(a => a.id !== answer.id);
    setFormStatus({ ...formStatus, error: false });
    setAnswersWithValidation(filteredAnswers.concat([answer]));
  };

  const IsQuestionsValid = (questions: ownApiAnswerWithValidation[]) => {
    const invalidQuestions = questions.filter(q => !q.isValid);
    return invalidQuestions.length <= 0;
  };

  useEffect(() => {
    if (startQuestionnaireData.status === 'REMOTE_DATA_SUCCESS') {
      handleSessionUpdate(startQuestionnaireData.data.session ? startQuestionnaireData.data.session : '');

      if (startQuestionnaireData.data.questions) {
        setQuestions(startQuestionnaireData.data.questions);
        setAnswersWithValidation(
          startQuestionnaireData.data.questions.map(q => {
            return {
              id: q.id,
              isValid: false,
            };
          }),
        );
      }
    }
    if (startQuestionnaireData.status === 'REMOTE_DATA_FAILURE') {
      sendAppError(`Apidata failed: ${startQuestionnaireData.error}`);
      sendMessage({
        type: 'Error',
        payload: { type: 'InternalError', message: `Apidata failed: ${startQuestionnaireData.error}` },
      });
      setFormStatus({ error: true, message: 'error_remote_data_failure' });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startQuestionnaireData]);

  /* 
    Handles click of "next"-button. Asks API for new question and either 
    adds the new question to questions array or advances to the result view.
  */
  const handleNextClick = () => {
    if (!IsQuestionsValid(answersWithValidation)) {
      setFormStatus({
        error: true,
        message: 'error_missing_information',
        invalidFields: answersWithValidation.filter(a => !a.isValid),
      });
      return;
    } else {
      setFormStatus(formStatusInit);
    }

    apiClient
      .getNewQuestion(
        authState.type === 'LoggedIn' && authState.accessToken ? authState.accessToken : '',
        selectedLanguage,
        session,
        answersWithValidation,
      )
      .then(data => {
        if (data.session && data.question) {
          handleSessionUpdate(data.session);
          setQuestions(questions.concat(data.question));
        } else {
          onClickNext(answersWithValidation);
        }
      })
      .catch(err => {
        setFormStatus({ error: true, message: 'error_remote_data_failure' });
        sendAppError('Apidata failed');
        sendMessage({ type: 'Error', payload: { type: 'InternalError', message: err } });
      });
  };

  return (
    <div className={classnames('flex', 'flex-col', 'mb-2')}>
      <ProgressBar currentStep={2} />
      <Title as="h1" size="xl" weight="normal" className={classnames('mb-1')}>
        {t('answer_to_questions')}
      </Title>
      {startQuestionnaireData.status === 'REMOTE_DATA_LOADING' && (
        <Question question={undefined} handleAnswerUpdate={handleQuestionUpdate} loading={true} error={false} />
      )}
      {questions.map((q, i) => (
        <Question
          key={q.id}
          question={q}
          handleAnswerUpdate={handleQuestionUpdate}
          loading={false}
          isNotLast={i + 1 < questions.length}
          error={formStatus.invalidFields ? !!formStatus.invalidFields.find(f => f.id === q.id) : false}
        />
      ))}
      {formStatus.error && formStatus.message && (
        <Alert icon="AlertOctagon" variant="error" role="region" aria-live="polite" className={classnames('mb-1.5')}>
          {t(formStatus.message)}
        </Alert>
      )}
      <Button
        id="next-button"
        as="button"
        type="button"
        icon="ArrowRight"
        disabled={formStatus.error}
        onClick={handleNextClick}
        className={classnames('mb-1')}
      >
        {t('next')}
      </Button>
    </div>
  );
};
